// Screen `object-list` — the list of an object's records in one of its saved views (design/screens/
// Main.dc.html, DealsTable.dc.html, PeopleTable.dc.html). Owner: views-grid. Search params:
// `view` (key or id), `d` (the unsaved view draft, lz-string; see features/views/grid/draft.ts) and
// `q` (search text) — reload, back/forward and copied links reproduce what is shown.
import { createFileRoute } from '@tanstack/react-router';
import { ErrorState, useIsCompact } from '@dataroom/ui';
import { ObjectListPage } from '../../../../features/views/ObjectListPage';
import { ListSearch } from '../../../../features/views/list-search';
import { t } from '../../../../lib/i18n';
import { Page } from '../../../../shell/Page';
import { NotFoundPage } from '../../../../shell/states';
import { useObject } from '../../../../shell/useObject';

export const Route = createFileRoute('/_app/o/$object/')({
  validateSearch: ListSearch,
  component: ObjectListRoute,
});

function ObjectListRoute() {
  const { object: key } = Route.useParams();
  const { view } = Route.useSearch();
  const { object, appLabel, loading, error, retry, retrying } = useObject(key);
  const mobile = useIsCompact();
  if (!object) {
    // The model loaded and has no such object: the same page and actions as a route that does not exist.
    if (!error && !loading)
      return (
        <NotFoundPage
          signedIn
          title={t('views.objectNotFound')}
          description={t('views.objectNotFoundText', { key })}
          query={key.replace(/[-_]+/g, ' ')}
        />
      );
    return (
      <Page crumbs={[{ label: key }]} testId="screen-object-list">
        {error ? (
          // The model did not load: the object is not "missing", the server is unreachable.
          <ErrorState
            error={error}
            title={t('views.grid.errorTitle')}
            description={t('views.grid.errorBody')}
            onRetry={retry}
            retrying={retrying}
          />
        ) : null}
      </Page>
    );
  }
  // Below 640px every object is listed by the phone list (src/overlays/mobile-list.tsx): the
  // desktop layout is not rendered underneath, so it neither queries nor sits in the a11y tree.
  if (mobile) return null;
  return <ObjectListPage key={object.key} object={object} appLabel={appLabel} viewKey={view} />;
}
